<template>
  <section v-if="loaded" class="onboarding" :class="{ _complete: complete }">
    <div class="onboarding__head">
      <span class="onboarding__icon">
        <svg v-svg symbol="satellite" size="0 0 16 16" role="presentation"></svg>
      </span>
      <div class="onboarding__heading">
        <h2 class="onboarding__title">{{ $t("satellites.onboarding.title") }}</h2>
        <div class="onboarding__progress">
          <span class="onboarding__bar"><span :style="{ width: `${(doneCount / steps.length) * 100}%` }"></span></span>
          {{ $t("satellites.onboarding.progress", { done: doneCount, total: steps.length }) }}
        </div>
      </div>
      <button v-if="complete" type="button" class="onboarding__toggle" :aria-expanded="open" @click="toggle">
        {{ open ? $t("satellites.onboarding.hide") : $t("satellites.onboarding.show") }}
        <SatIcon name="chevron" :size="16" class="onboarding__chevron" :class="{ _open: open }" />
      </button>
    </div>

    <div v-show="open" class="onboarding__body">
      <p class="onboarding__intro">{{ $t("satellites.onboarding.intro") }}</p>
      <div class="onboarding__flow">
        <template v-for="(item, index) in flow">
          <SatIcon v-if="index" :key="`arrow-${item.key}`" name="chevron-right" :size="13" class="onboarding__arrow" />
          <span :key="item.key" class="sat-chip"><SatIcon :name="item.icon" :size="12" /> {{ $t(`satellites.tabs.${item.key}`) }}</span>
        </template>
      </div>

      <ol class="onboarding__steps">
        <li v-for="(step, index) in steps" :key="step.key" class="onboarding-step" :class="{ _done: step.done, _current: current === step }">
          <span class="onboarding-step__mark">
            <SatIcon v-if="step.done" name="check" :size="14" />
            <template v-else>{{ index + 1 }}</template>
          </span>
          <div class="onboarding-step__body">
            <div class="onboarding-step__title">{{ $t(`satellites.onboarding.steps.${step.key}.title`) }}</div>
            <div class="onboarding-step__text">{{ $t(`satellites.onboarding.steps.${step.key}.text`) }}</div>
            <div v-if="step.pending" class="onboarding-step__pending">{{ $t(`satellites.onboarding.steps.${step.key}.pending`) }}</div>
          </div>
          <div v-if="!step.done" class="onboarding-step__actions">
            <template v-if="step.key === 'satellite'">
              <button v-if="installCommand" type="button" class="btn _border _green onboarding-step__btn" @click="$emit('install', installCommand)">
                {{ $t("satellites.install_command") }}
              </button>
              <button v-else-if="!step.pending" type="button" class="btn _bg-green onboarding-step__btn" :disabled="creating" @click="$emit('create')">
                <img v-if="creating" src="@/assets/images/rolling-button.svg" class="loader-button" alt="loader" />
                {{ $t("satellites.create") }}
              </button>
            </template>
            <template v-else>
              <router-link v-if="step.extra" :to="step.extra" class="btn _border _green onboarding-step__btn">{{ $t(`satellites.onboarding.steps.${step.key}.extra`) }}</router-link>
              <router-link :to="step.to" class="btn _bg-green onboarding-step__btn">{{ $t(`satellites.onboarding.steps.${step.key}.action`) }}</router-link>
            </template>
          </div>
        </li>
      </ol>
    </div>

    <!-- The goal of the setup: a link to send the traffic to -->
    <div v-if="complete || open" class="onboarding-link" :class="{ _locked: !complete }">
      <div class="onboarding-link__title"><SatIcon name="link" :size="16" /> {{ $t("satellites.onboarding.link.title") }}</div>
      <div v-if="!complete" class="onboarding-link__note">{{ $t("satellites.onboarding.link.locked") }}</div>
      <div v-else-if="linkState === 'loading'" class="onboarding-link__note">
        <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="onboarding-link__loader" />
      </div>
      <template v-else-if="link">
        <div class="onboarding-link__url">
          <span v-for="(part, index) in link.parts" :key="index" :class="`_${part.kind}`">{{ part.text }}</span>
        </div>
        <div class="onboarding-link__actions">
          <button type="button" class="btn _bg-green sat-btn-inline onboarding-link__copy" @click="copy">
            <SatIcon :name="copied ? 'check' : 'copy'" />
            {{ copied ? $t("satellites.links.copied") : $t("satellites.links.copy") }}
          </button>
          <a :href="link.url" target="_blank" rel="noopener noreferrer" class="btn _border _green sat-btn-inline"><SatIcon name="external" /> {{ $t("satellites.links.open_link") }}</a>
          <router-link :to="{ name: 'SatelliteLinks', query: link.query }" class="sat-link onboarding-link__customize">{{ $t("satellites.onboarding.link.customize") }}</router-link>
        </div>
      </template>
      <div v-else class="onboarding-link__note">
        {{ $t("satellites.onboarding.link.unavailable") }}
        <router-link :to="{ name: 'SatelliteLinks' }" class="sat-link">{{ $t("satellites.tabs.links") }}</router-link>
      </div>
    </div>
  </section>
</template>
<script>
import satelliteService from "@/api/satelliteService";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import { isOk } from "@/components/Satellites/format";
import { linkParts } from "@/components/Satellites/trafficCheck";

const STORAGE_KEY = "satellites.onboarding.open";

const readOpen = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === null ? null : value === "1";
  } catch (e) {
    return null;
  }
};

// Setup checklist of the satellites section: explains how Calypso works, ticks the steps off by the data of the user
// and, once they are all done, builds a ready link (the first route of the first domain with a router).
export default {
  name: "SatellitesOnboarding",
  components: { SatIcon },
  props: {
    // Rows of the satellites page, polled there: a satellite going online ticks its step off without a reload
    satellites: {
      type: Array,
      default: () => [],
    },
    creating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loaded: false,
      domains: [],
      landingsCount: 0,
      platforms: [],
      // Choice of the user for the finished checklist; an unfinished one is always open
      openChoice: readOpen(),
      linkState: "idle",
      link: null,
      copied: false,
      // The order of the setup, see SatellitesTabs
      flow: [
        { key: "satellites", icon: "server" },
        { key: "domains", icon: "globe" },
        { key: "landings", icon: "file" },
        { key: "platforms", icon: "grid" },
        { key: "routers", icon: "route" },
        { key: "links", icon: "link" },
      ],
    };
  },
  computed: {
    steps() {
      const online = this.satellites.some((s) => s.status === "online");
      return [
        // Pending: created, but none of the satellites is online yet
        { key: "satellite", done: online, pending: !online && this.satellites.length > 0 },
        { key: "domain", done: this.domains.length > 0, to: { name: "SatelliteDomains", query: { add: "1" } } },
        { key: "landing", done: this.landingsCount > 0, to: { name: "SatelliteLandings" } },
        { key: "platform", done: this.platforms.length > 0, to: { name: "SatellitePlatforms" } },
        // A router is chosen for a domain on the domains page
        { key: "router", done: this.domains.some((d) => d.router), to: { name: "SatelliteRouters", query: { create: "1" } }, extra: { name: "SatelliteDomains" } },
      ];
    },
    doneCount() {
      return this.steps.filter((step) => step.done).length;
    },
    complete() {
      return this.doneCount === this.steps.length;
    },
    current() {
      return this.steps.find((step) => !step.done) || null;
    },
    open() {
      return !this.complete || this.openChoice === true;
    },
    // The command of a satellite still waiting for the install
    installCommand() {
      const satellite = this.satellites.find((s) => s.install_command);
      return satellite ? satellite.install_command : "";
    },
  },
  watch: {
    complete(value) {
      if (value) this.loadLink();
    },
  },
  mounted() {
    Promise.all([satelliteService.getDomains(), satelliteService.getLandings(), satelliteService.getPlatforms()])
      .then(([domains, landings, platforms]) => {
        // A failed request leaves its step undone: the checklist is a hint, not a place for errors
        if (isOk(domains)) this.domains = (domains.data && domains.data.domains) || [];
        if (isOk(landings)) this.landingsCount = (landings.data && landings.data.count) || 0;
        if (isOk(platforms)) this.platforms = (platforms.data && platforms.data.platforms) || [];
      })
      .finally(() => {
        this.loaded = true;
        if (this.complete) this.loadLink();
      });
  },
  beforeDestroy() {
    clearTimeout(this.copiedTimer);
  },
  methods: {
    toggle() {
      this.openChoice = !this.open;
      try {
        localStorage.setItem(STORAGE_KEY, this.openChoice ? "1" : "0");
      } catch (e) {
        // The choice is just not remembered
      }
    },
    loadLink() {
      const domain = this.domains.find((d) => d.router);
      if (!domain || this.linkState !== "idle") return;
      this.linkState = "loading";
      satelliteService.getDomainTraffic(domain.id).then((res) => {
        const router = isOk(res) && res.data ? res.data.router : null;
        const route = router && router.routes.length ? router.routes[0] : null;
        const platform = route && route.platform.mode === "param" ? this.platforms[0] : null;
        if (!route || (route.platform.mode === "param" && !platform)) {
          this.linkState = "unavailable";
          return;
        }
        const platformId = platform ? platform.id : null;
        const parts = linkParts(res.data.domain.host, route, platformId, []);
        this.link = {
          parts,
          url: parts.map((part) => part.text).join(""),
          // The same choice in the links section
          query: { domain: domain.id, path: route.path, platform: platformId || undefined },
        };
        this.linkState = "ready";
      });
    },
    copy() {
      navigator.clipboard.writeText(this.link.url).then(() => {
        this.copied = true;
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => (this.copied = false), 1800);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.onboarding {
  margin-bottom: 20px;
  padding: 22px 26px;
  border-radius: 16px;
  background: var(--sat-surface);

  @media (max-width: 767px) {
    padding: 18px 16px;
  }
}

.onboarding__head {
  display: flex;
  align-items: center;
  gap: 14px;
}

.onboarding__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 2px dashed var(--sat-accent-soft);
  background: var(--sat-accent-soft);

  svg {
    width: 24px;
    height: 24px;
    fill: var(--sat-accent);
  }
}

.onboarding__heading {
  flex: 1;
  min-width: 0;
}

.onboarding__title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: var(--sat-text);
}

.onboarding__progress {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--sat-muted);
}

.onboarding__bar {
  width: 140px;
  height: 6px;
  border-radius: 3px;
  background: var(--sat-border);

  span {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: var(--sat-accent);
    transition: width 0.3s;
  }

  ._complete & span {
    background: var(--sat-ok);
  }
}

.onboarding__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--sat-accent);
  background: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }
}

.onboarding__chevron {
  transition: transform 0.2s;

  &._open {
    transform: rotate(180deg);
  }
}

.onboarding__body {
  margin-top: 18px;
}

.onboarding__intro {
  max-width: 860px;
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--sat-text-2);
}

.onboarding__flow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.onboarding__arrow {
  color: var(--sat-muted);
}

.onboarding__steps {
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.onboarding-step {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: var(--sat-card);

  & + & {
    margin-top: 8px;
  }

  &._current {
    border-color: var(--sat-accent);
  }

  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
}

.onboarding-step__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  color: var(--sat-accent);
  background: var(--sat-accent-soft);

  ._done & {
    color: var(--sat-ok);
    background: var(--sat-ok-soft);
  }
}

.onboarding-step__body {
  flex: 1;
  min-width: 0;
}

.onboarding-step__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--sat-text);

  ._done & {
    color: var(--sat-muted);
    text-decoration: line-through;
  }
}

.onboarding-step__text {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--sat-text-2);

  ._done & {
    color: var(--sat-muted);
  }
}

.onboarding-step__pending {
  margin-top: 6px;
  font-size: 13px;
  color: var(--sat-warn);
}

.onboarding-step__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;
    padding-left: 40px;
  }
}

.onboarding-step__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  font-size: 13px;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    text-decoration: none;
  }
}

.onboarding-link {
  margin-top: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid var(--sat-border);
  background: var(--sat-code);

  &._locked {
    border-style: dashed;
    background: transparent;
  }
}

.onboarding-link__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--sat-text);

  ._locked & {
    color: var(--sat-muted);
  }
}

.onboarding-link__note {
  margin-top: 6px;
  font-size: 13px;
  color: var(--sat-muted);
}

.onboarding-link__loader {
  width: 24px;
  height: 24px;
}

.onboarding-link__url {
  margin-top: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 15px;
  line-height: 1.6;
  word-break: break-all;
  color: var(--sat-text);

  ._base {
    font-weight: 600;
  }

  ._path {
    color: var(--sat-accent);
    font-weight: 700;
  }

  ._sep,
  ._key {
    color: var(--sat-muted);
  }

  ._value {
    font-weight: 600;
  }
}

.onboarding-link__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;

  .btn {
    height: 40px;
  }
}

.onboarding-link__copy {
  min-width: 160px;
  justify-content: center;
}

.onboarding-link__customize {
  font-size: 13px;
}
</style>
